"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Découpez la liste et voyez ce qui sort",
        start: "Début",
        stop: "Fin",
        step: "Pas",
        result: "Résultat",
        empty: "liste vide",
        note: "Les rangs négatifs comptent depuis la fin. Le découpage ne lève jamais d'erreur, même hors limites : c'est ce qui le distingue de l'accès direct par rang.",
        forward: "Le pas est positif, la lecture va de gauche à droite et s'arrête avant la borne de fin, qui est exclue.",
        backward: "Le pas est négatif, la lecture part de la fin vers le début. Avec ce sens, la borne de fin reste exclue elle aussi, ce qui surprend tout le monde la première fois.",
        emptyWhy: "Rien ne sort : la borne de départ est déjà au-delà de la borne d'arrivée dans le sens du parcours. Python ne s'en plaint pas, il rend une liste vide.",
        skip: "Un pas supérieur à 1 saute des éléments sans jamais dépasser la borne de fin.",
    },
    en: {
        title: "Slice the list and watch what comes out",
        start: "Start",
        stop: "Stop",
        step: "Step",
        result: "Result",
        empty: "empty list",
        note: "Negative positions count from the end. Slicing never raises an error, even out of bounds: that is what sets it apart from direct access by position.",
        forward: "The step is positive, reading runs left to right and stops before the end bound, which is excluded.",
        backward: "The step is negative, reading runs from the end towards the start. In that direction the end bound stays excluded too, which surprises everyone the first time.",
        emptyWhy: "Nothing comes out: the start bound already sits past the stop bound in the direction of travel. Python does not complain, it hands back an empty list.",
        skip: "A step greater than 1 skips items without ever passing the end bound.",
    },
};

const LETTRES = ["a", "b", "c", "d", "e", "f", "g", "h"];

/**
 * Reproduit la sémantique de Python, y compris le pas négatif et les bornes
 * hors limites. Une implémentation approximative ferait mentir la démo, qui
 * n'aurait alors plus aucun intérêt.
 */
function decouper(arr, debut, fin, pas) {
    const n = arr.length;
    let i = debut < 0 ? debut + n : debut;
    let j = fin < 0 ? fin + n : fin;
    const sortie = [];

    if (pas > 0) {
        i = Math.max(0, Math.min(i, n));
        j = Math.max(0, Math.min(j, n));
        for (let k = i; k < j; k += pas) sortie.push(k);
    } else {
        i = Math.max(-1, Math.min(i, n - 1));
        j = Math.max(-1, Math.min(j, n - 1));
        for (let k = i; k > j; k += pas) sortie.push(k);
    }
    return sortie;
}

export default function SlicingPlayground({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [debut, setDebut] = useState(1);
    const [fin, setFin] = useState(6);
    const [pas, setPas] = useState(1);

    const rangs = useMemo(() => decouper(LETTRES, debut, fin, pas), [debut, fin, pas]);
    const retenus = new Set(rangs);

    const explication = rangs.length === 0 ? t.emptyWhy : pas < 0 ? t.backward : pas > 1 ? t.skip : t.forward;

    return (
        <Frame icon="scissors-square-linear" title={t.title}>
            <div className="font-geistMono text-sm mb-4 dark:text-white">
                lettres[{debut}:{fin}:{pas}]
            </div>

            <div className="flex gap-1 mb-1 overflow-x-auto pb-1">
                {LETTRES.map((lettre, i) => (
                    <div key={i} className="shrink-0 text-center" style={{ width: 40 }}>
                        <div
                            className={`rounded-lg py-2 font-geistMono text-sm transition-colors duration-150 ${
                                retenus.has(i)
                                    ? "bg-primary text-white font-bold"
                                    : "bg-gray-100 dark:bg-white/[0.06] text-gray-400 dark:text-grayDark-500"
                            }`}
                        >
                            {lettre}
                        </div>
                        <div className="mt-1 font-geistMono text-[10px] text-gray-400 dark:text-grayDark-500">
                            {i}
                        </div>
                        <div className="font-geistMono text-[10px] text-gray-300 dark:text-grayDark-600">
                            {i - LETTRES.length}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-5 space-y-4">
                <Slider label={t.start} value={debut} onChange={setDebut} min={-8} max={8} />
                <Slider label={t.stop} value={fin} onChange={setFin} min={-8} max={8} />
                <Slider label={t.step} value={pas} onChange={(v) => setPas(v === 0 ? 1 : v)} min={-3} max={3} />
            </div>

            <div className="mt-5 rounded-xl bg-gray-50 dark:bg-white/[0.04] px-4 py-3">
                <div className="text-xs uppercase tracking-widest text-gray-400 dark:text-grayDark-500 mb-1">
                    {t.result}
                </div>
                <div className="font-geistMono text-sm dark:text-white">
                    {rangs.length ? `[${rangs.map((i) => `'${LETTRES[i]}'`).join(", ")}]` : `[] ${t.empty}`}
                </div>
            </div>

            <Verdict tone={rangs.length === 0 ? "warn" : "neutral"}>{explication}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
